import { FormEvent, useEffect, useState, type ReactNode } from 'react'
import { ultraadminPartnersApi, type PartnerCompanyAdmin } from '@/services/api/partner'
import { ultraadminApi, type CountryInfo } from '@/services/api/ultraadmin'
import { Modal } from '@/components/common'
import { extractErrorMessage } from '@/utils/errorHandler'


type EditForm = {
  name: string
  address: string
  zip_code: string
  city: string
  country: string
  phone: string
  email: string
  commission_rate: string
  notes: string
}

const emptyEdit = (): EditForm => ({
  name: '',
  address: '',
  zip_code: '',
  city: '',
  country: '',
  phone: '',
  email: '',
  commission_rate: '',
  notes: '',
})

function blank(value: string): string | null {
  const t = value.trim()
  return t === '' ? null : t
}

function parseCommission(raw: string): number | null {
  const t = raw.trim()
  if (t === '') return null
  const n = Number(t)
  if (!Number.isFinite(n) || n < 0 || n > 100) {
    throw new Error('Provision muss eine Zahl zwischen 0 und 100 sein')
  }
  return n
}

export function UltraadminPartnersPage() {
  const [rows, setRows] = useState<PartnerCompanyAdmin[]>([])
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [rate, setRate] = useState('')
  const [editing, setEditing] = useState<PartnerCompanyAdmin | null>(null)
  const [editForm, setEditForm] = useState<EditForm>(emptyEdit())
  const [teamId, setTeamId] = useState<string | null>(null)
  const [memberEmail, setMemberEmail] = useState('')
  const [memberFirst, setMemberFirst] = useState('')
  const [memberLast, setMemberLast] = useState('')
  const [memberLevel, setMemberLevel] = useState<'owner' | 'staff'>('staff')
  const [countries, setCountries] = useState<CountryInfo[]>([])

  const load = async () => setRows(await ultraadminPartnersApi.list())
  useEffect(() => { load().catch((e) => setError(extractErrorMessage(e, { loadError: 'Fehler' }))) }, [])
  useEffect(() => {
    ultraadminApi.getCountries().then(setCountries).catch(() => undefined)
  }, [])

  const teamCompany = rows.find((x) => x.id === teamId) || null

  const openEdit = (row: PartnerCompanyAdmin) => {
    setEditing(row)
    setEditForm({
      name: row.name,
      address: row.address || '',
      zip_code: row.zip_code || '',
      city: row.city || '',
      country: row.country || '',
      phone: row.phone || '',
      email: row.email || '',
      commission_rate: row.commission_rate == null ? '' : String(row.commission_rate),
      notes: row.notes || '',
    })
  }

  const onCreate = async (e: FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      await ultraadminPartnersApi.create({
        name,
        commission_rate: parseCommission(rate),
        owner: { email, first_name: firstName, last_name: lastName },
      })
      setName(''); setEmail(''); setFirstName(''); setLastName(''); setRate('')
      setError(null)
      await load()
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Anlegen fehlgeschlagen' }))
    } finally {
      setSubmitting(false)
    }
  }

  const onSaveEdit = async (e: FormEvent) => {
    e.preventDefault()
    if (!editing) return
    setSubmitting(true)
    try {
      const updated = await ultraadminPartnersApi.update(editing.id, {
        name: editForm.name.trim(),
        address: blank(editForm.address),
        zip_code: blank(editForm.zip_code),
        city: blank(editForm.city),
        country: blank(editForm.country),
        phone: blank(editForm.phone),
        email: blank(editForm.email),
        notes: blank(editForm.notes),
        commission_rate: parseCommission(editForm.commission_rate),
      })
      setEditing(null)
      setError(null)
      setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)))
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Speichern fehlgeschlagen' }))
    } finally {
      setSubmitting(false)
    }
  }

  const runMember = async (fn: () => Promise<unknown>) => {
    try {
      await fn()
      setError(null)
      await load()
    } catch (err: unknown) {
      setNotice(null)
      setError(extractErrorMessage(err, { loadError: 'Fehler' }))
    }
  }

  const field = (label: string, description: string, children: ReactNode) => (
    <div>
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <p className="text-xs text-gray-500 mt-0.5">{description}</p>
      <div className="mt-1">{children}</div>
    </div>
  )

  const inputCls = 'block w-full rounded-md border border-gray-300 shadow-sm px-3 py-2'

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">Partner-Firmen</h1>
      {error && <div className="p-3 bg-red-100 text-red-700 rounded">{error}</div>}
      {notice && <div className="p-3 bg-blue-50 text-blue-800 rounded">{notice}</div>}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">E-Mail</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ort</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provision %</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aktionen</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="px-6 py-4">{r.name}</td>
                <td className="px-6 py-4">{r.email || '—'}</td>
                <td className="px-6 py-4">{[r.zip_code, r.city].filter(Boolean).join(' ') || '—'}</td>
                <td className="px-6 py-4">{r.commission_rate ?? '—'}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 text-xs rounded-full ${r.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {r.is_active ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm">
                  <div className="flex flex-wrap items-center gap-3">
                    <button type="button" className="text-blue-600 hover:text-blue-900" onClick={() => openEdit(r)}>Bearbeiten</button>
                    <button type="button" className="text-blue-600 hover:text-blue-900" onClick={() => setTeamId(r.id)}>Team</button>
                    {r.is_active && (
                      <button
                        type="button"
                        className="px-3 py-1 text-sm text-red-600 hover:text-red-900 border border-red-200 rounded"
                        onClick={() => {
                          if (!confirm(`Firma "${r.name}" deaktivieren? Alle Logins sterben, Schulen werden Direkt.`)) return
                          runMember(() => ultraadminPartnersApi.deactivate(r.id))
                        }}
                      >
                        Deaktivieren
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <form onSubmit={onCreate} className="bg-white p-4 rounded-lg shadow max-w-md space-y-2">
        <h2 className="font-semibold">Firma + erster Inhaber</h2>
        <input className="border w-full p-2" placeholder="Firmenname" value={name} onChange={(e) => setName(e.target.value)} required />
        <input className="border w-full p-2" placeholder="Provision % (optional)" value={rate} onChange={(e) => setRate(e.target.value)} />
        <input className="border w-full p-2" placeholder="Inhaber Vorname" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
        <input className="border w-full p-2" placeholder="Inhaber Nachname" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
        <input className="border w-full p-2" placeholder="Inhaber E-Mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <button type="submit" disabled={submitting} className="bg-blue-600 text-white px-3 py-1 rounded disabled:opacity-50">Anlegen</button>
      </form>

      <Modal isOpen={!!editing} onClose={() => setEditing(null)} title={editing ? `Firma bearbeiten: ${editing.name}` : 'Firma bearbeiten'} size="lg">
        {editing && (
          <form onSubmit={onSaveEdit} className="space-y-4">
            {field('Firmenname *', 'Anzeigename der Partner-Firma. Muss eindeutig sein.',
              <input className={inputCls} value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required />)}
            {field('Adresse', 'Strasse und Hausnummer der Firma.',
              <input className={inputCls} value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} />)}
            <div className="grid grid-cols-2 gap-4">
              {field('PLZ', 'Postleitzahl.',
                <input className={inputCls} value={editForm.zip_code} onChange={(e) => setEditForm({ ...editForm, zip_code: e.target.value })} />)}
              {field('Ort', 'Ort oder Stadt.',
                <input className={inputCls} value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} />)}
            </div>
            <div className="grid grid-cols-2 gap-4">
              {field('Land', 'Land der Firma. Wird als ISO-Code gespeichert.',
                <select className={inputCls} value={editForm.country} onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}>
                  <option value="">—</option>
                  {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                  {editForm.country && !countries.some((c) => c.code === editForm.country) && (
                    <option value={editForm.country}>{editForm.country}</option>
                  )}
                </select>)}
              {field('Telefon', 'Kontakttelefon der Firma.',
                <input className={inputCls} value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />)}
            </div>
            {field('E-Mail', 'Kontaktadresse der Firma, nicht der Login.',
              <input className={inputCls} type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />)}
            {field('Provision %', 'Anteil des Partners an der Schulrechnung. Nur Ultraadmin. Leer = noch nicht gesetzt.',
              <input className={inputCls} value={editForm.commission_rate} onChange={(e) => setEditForm({ ...editForm, commission_rate: e.target.value })} />)}
            {field('Notizen', 'Intern, nur für Ultraadmin sichtbar. Partner sehen diesen Text nicht.',
              <textarea className={inputCls} rows={3} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} />)}
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 border border-gray-300 rounded-md">Abbrechen</button>
              <button type="submit" disabled={submitting} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                {submitting ? 'Speichern…' : 'Speichern'}
              </button>
            </div>
          </form>
        )}
      </Modal>

      <Modal isOpen={!!teamCompany} onClose={() => setTeamId(null)} title={teamCompany ? `Team: ${teamCompany.name}` : 'Team'} size="lg">
        {teamCompany && (
          <div className="space-y-4">
            <ul className="text-sm space-y-2">
              {teamCompany.members.map((m) => (
                <li key={m.id} className="flex flex-wrap gap-2 items-center">
                  <span>{m.last_name}, {m.first_name} — {m.email} ({m.level === 'owner' ? 'Inhaber' : 'Mitarbeiter'}{m.is_active ? '' : ', inaktiv'})</span>
                  {m.level === 'staff' && (
                    <button type="button" className="text-blue-600" onClick={() => runMember(() => ultraadminPartnersApi.updateMember(teamCompany.id, m.user_id, { level: 'owner' }))}>Inhaber</button>
                  )}
                  {m.level === 'owner' && (
                    <button type="button" className="text-blue-600" onClick={() => runMember(() => ultraadminPartnersApi.updateMember(teamCompany.id, m.user_id, { level: 'staff' }))}>Mitarbeiter</button>
                  )}
                  {m.is_active
                    ? <button type="button" className="text-red-600" onClick={() => runMember(() => ultraadminPartnersApi.updateMember(teamCompany.id, m.user_id, { is_active: false }))}>Deaktivieren</button>
                    : <button type="button" className="text-green-700" onClick={() => runMember(() => ultraadminPartnersApi.updateMember(teamCompany.id, m.user_id, { is_active: true }))}>Reaktivieren</button>}
                </li>
              ))}
            </ul>
            <form className="flex flex-wrap gap-2" onSubmit={(e) => {
              e.preventDefault()
              runMember(async () => {
                await ultraadminPartnersApi.inviteMember(teamCompany.id, {
                  email: memberEmail, first_name: memberFirst, last_name: memberLast, level: memberLevel,
                })
                setMemberEmail(''); setMemberFirst(''); setMemberLast('')
              })
            }}>
              <input className="border p-1" placeholder="Vorname" value={memberFirst} onChange={(e) => setMemberFirst(e.target.value)} required />
              <input className="border p-1" placeholder="Nachname" value={memberLast} onChange={(e) => setMemberLast(e.target.value)} required />
              <input className="border p-1" placeholder="E-Mail" type="email" value={memberEmail} onChange={(e) => setMemberEmail(e.target.value)} required />
              <select className="border p-1" value={memberLevel} onChange={(e) => setMemberLevel(e.target.value as 'owner' | 'staff')}>
                <option value="staff">Mitarbeiter</option>
                <option value="owner">Inhaber</option>
              </select>
              <button type="submit" className="bg-blue-600 text-white px-2 rounded">Einladen</button>
            </form>
          </div>
        )}
      </Modal>
    </div>
  )
}
